/**
 * Labels of the motion columns (W2-04a): property names, friendly values, easing badges and text segment modes
 * (DESIGN.md §2.5 vocabulary, §4.7 badges "plain words Guided / ids Full", §6.8 text modes, §6.9 units).
 */

import { Ease } from '../../core/ease/index';
import type { Ease as EaseValue } from '../../core/ease/types';
import { textSegmentMode } from '../../core/commands/text';
import { Units } from '../../core/model/units';
import type { LineKind, LineProp } from '../../core/model/types';
import type { ExperienceLevel } from '../../state/types';
import type { MessageKey } from '../../i18n/types';
import { m } from './motion.messages';

export type Translate = (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;

export interface PropContext {
  readonly kind: LineKind;
  readonly uiMover: boolean;
}

/** Full, short (header) and tiny (narrow header, UX-034) names of a property on a line kind. */
export function propNames(
  prop: LineProp,
  ctx: PropContext,
): { readonly full: MessageKey; readonly short: MessageKey; readonly tiny: MessageKey } {
  const scaled = ctx.kind !== 'normal' || ctx.uiMover;
  switch (prop) {
    case 'moveX':
      return { full: m.propMoveX, short: m.shortMoveX, tiny: m.tinyMoveX };
    case 'moveY':
      return { full: m.propMoveY, short: m.shortMoveY, tiny: m.tinyMoveY };
    case 'rotate':
      return { full: m.propRotate, short: m.shortRotate, tiny: m.tinyRotate };
    case 'alpha':
      return { full: m.propAlpha, short: m.shortAlpha, tiny: m.tinyAlpha };
    case 'speed':
      return { full: m.propSpeed, short: m.shortSpeed, tiny: m.tinySpeed };
    case 'scaleX':
      return scaled
        ? { full: m.propSizeX, short: m.shortSizeX, tiny: m.tinySizeX }
        : { full: m.propLength, short: m.shortLength, tiny: m.tinyLength };
    case 'scaleY':
      return scaled
        ? { full: m.propSizeY, short: m.shortSizeY, tiny: m.tinySizeY }
        : { full: m.propThickness, short: m.shortThickness, tiny: m.tinyThickness };
    case 'color':
      return { full: m.propColor, short: m.shortColor, tiny: m.tinyColor };
    case 'text':
      return { full: m.propText, short: m.shortText, tiny: m.tinyText };
    case 'incline':
      return { full: m.propIncline, short: m.shortIncline, tiny: m.tinyIncline };
    case 'paint':
      return { full: m.propPaint, short: m.shortPaint, tiny: m.tinyPaint };
    case 'gif':
      return { full: m.propGif, short: m.shortGif, tiny: m.tinyGif };
  }
}

/**
 * Name of a property without a line in view (palette argument lists): the line-kind dependent ones say both names
 * ("Length / Size X").
 */
export function neutralPropName(prop: LineProp): MessageKey {
  if (prop === 'scaleX') return m.propScaleXAny;
  if (prop === 'scaleY') return m.propScaleYAny;
  return propNames(prop, { kind: 'normal', uiMover: false }).full;
}

/** Name of a property across several lines: their common name, else the neutral one. */
export function propNameAcross(prop: LineProp, ctxs: readonly PropContext[]): MessageKey {
  const first = ctxs[0];
  if (first === undefined) return neutralPropName(prop);
  const name = propNames(prop, first).full;
  return ctxs.every((c) => propNames(prop, c).full === name) ? name : neutralPropName(prop);
}

/** Label of a pair sub-column ("X", "Y", "W", "H"). */
export function subLabelKey(sub: 'X' | 'Y' | 'W' | 'H'): MessageKey {
  switch (sub) {
    case 'X':
      return m.subX;
    case 'Y':
      return m.subY;
    case 'W':
      return m.subW;
    case 'H':
      return m.subH;
  }
}

/** A text value as labels show it (quoted). */
export function quoteText(t: Translate, text: string): string {
  return t(m.valueText, { text });
}

/** A colour as labels show it ("[255, 128, 0]", channels 0..255). */
export function formatRgb(t: Translate, rgb: readonly number[]): string {
  return t(m.valueRgb, { r: rgb[0] ?? 0, g: rgb[1] ?? 0, b: rgb[2] ?? 0 });
}

/** Joins labels with the locale's list separator (" · "). */
export function joinList(t: Translate, parts: readonly string[]): string {
  let out = parts[0] ?? '';
  for (let i = 1; i < parts.length; i++) out = t(m.listJoin, { a: out, b: parts[i] ?? '' });
  return out;
}

/** RPE / community term of a property (tooltips). */
export function rpeTerm(prop: LineProp): MessageKey {
  switch (prop) {
    case 'moveX':
      return m.rpeTermMoveX;
    case 'moveY':
      return m.rpeTermMoveY;
    case 'rotate':
      return m.rpeTermRotate;
    case 'alpha':
      return m.rpeTermAlpha;
    case 'speed':
      return m.rpeTermSpeed;
    case 'scaleX':
      return m.rpeTermScaleX;
    case 'scaleY':
      return m.rpeTermScaleY;
    case 'color':
      return m.rpeTermColor;
    case 'text':
      return m.rpeTermText;
    case 'incline':
      return m.rpeTermIncline;
    case 'paint':
      return m.rpeTermPaint;
    case 'gif':
      return m.rpeTermGif;
  }
}

/** A number with at most `decimals` decimals, without trailing zeros ("12.5", "100", "-0.25"). */
export function formatNumber(v: number, decimals = 2): string {
  if (!Number.isFinite(v)) return String(v);
  const f = 10 ** decimals;
  const r = Math.round(v * f) / f;
  const s = (Object.is(r, -0) ? 0 : r).toFixed(decimals);
  return decimals === 0 ? s : s.replace(/\.?0+$/, '');
}

/** Decimals shown for a friendly unit. */
function decimalsFor(unit: ReturnType<typeof Units.unitFor>): number {
  switch (unit) {
    case 'px':
    case 'deg':
    case 'brushPx':
      return 2;
    case 'opacityPercent':
    case 'lengthPercent':
    case 'sizePercent':
    case 'progressPercent':
      return 1;
    case 'speed':
      return 2;
    case 'thickness':
    case 'raw':
      return 3;
  }
}

/** Friendly display of a raw value with its unit ("100%", "15°", "-120", "×2"). */
export function formatValue(t: Translate, prop: LineProp, raw: number, ctx: PropContext): string {
  if (!Number.isFinite(raw)) return t(m.headerNotSet);
  const uctx = { kind: ctx.kind, uiMover: ctx.uiMover };
  const unit = Units.unitFor(prop, uctx);
  const v = formatNumber(Units.toFriendly(prop, raw, uctx), decimalsFor(unit));
  switch (unit) {
    case 'deg':
      return t(m.unitDeg, { v });
    case 'opacityPercent':
    case 'lengthPercent':
    case 'sizePercent':
    case 'progressPercent':
      return t(m.unitPercent, { v });
    case 'thickness':
      return t(m.unitThickness, { v });
    case 'speed':
      return t(m.unitSpeed, { v });
    case 'px':
    case 'brushPx':
      return t(m.unitPx, { v });
    case 'raw':
      return t(m.unitRaw, { v });
  }
}

/** One friendly step of a property in raw units (nudges; Shift = 10 steps). */
export function valueStep(prop: LineProp, ctx: PropContext): number {
  const uctx = { kind: ctx.kind, uiMover: ctx.uiMover };
  const unit = Units.unitFor(prop, uctx);
  const friendlyStep = unit === 'speed' || unit === 'thickness' ? 0.1 : 1;
  return Math.abs(Units.fromFriendly(prop, friendlyStep, uctx) - Units.fromFriendly(prop, 0, uctx));
}

const STRENGTH_KEYS = {
  gentle: m.strengthGentle,
  soft: m.strengthSoft,
  medium: m.strengthMedium,
  strong: m.strengthStrong,
  stronger: m.strengthStronger,
  extreme: m.strengthExtreme,
  sharp: m.strengthSharp,
  overshoot: m.strengthOvershoot,
  springy: m.strengthSpringy,
  bouncy: m.strengthBouncy,
} as const;

const SHAPE_KEYS = { slowStart: m.shapeSlowStart, slowEnd: m.shapeSlowEnd, slowBoth: m.shapeSlowBoth } as const;

function isWhole(e: Extract<EaseValue, { kind: 'tween' }>): boolean {
  return e.left <= 0 && e.right >= 1;
}

/**
 * Easing badge text of a segment: Guided plain words ("Soft, slow end", "Stay, then jump"), Full the RPE id
 * ("4", "4 part", "Stay", "Bz"). Linear gets no badge in Full (the default curve) unless `always`.
 */
export function easeBadge(
  t: Translate,
  e: EaseValue,
  level: ExperienceLevel,
  always = false,
  narrow = false,
): string | null {
  const guided = level === 'guided';
  switch (e.kind) {
    case 'hold':
      return t(guided ? m.easeHold : m.easeHoldShort);
    case 'bezier':
      return t(guided ? m.easeBezier : m.easeBezierShort);
    case 'tween': {
      const entry = Ease.catalog(e.id);
      const whole = isWhole(e);
      if (entry === null || entry.family === 'linear') {
        if (!guided && !always && whole) return null;
        return entry === null ? t(m.easeId, { id: e.id }) : guided ? t(m.easeLinear) : t(m.easeId, { id: e.id });
      }
      if (!guided) return t(whole ? m.easeId : m.easeIdSlice, { id: e.id });
      if (entry.strength === null || entry.shape === null) return t(m.easeId, { id: e.id });
      // A narrow lane keeps the part that tells how it moves ("slow end"), not "Medi..." (UX-034).
      if (narrow && whole) return t(SHAPE_KEYS[entry.shape]);
      return t(whole ? m.easeWords : m.easeWordsSlice, {
        strength: t(STRENGTH_KEYS[entry.strength]),
        shape: t(SHAPE_KEYS[entry.shape]),
      });
    }
  }
}

/** True for an eased (not linear, not hold) segment. */
export function isEased(e: EaseValue): boolean {
  if (e.kind === 'hold') return false;
  if (e.kind === 'bezier') return true;
  return e.id !== 1;
}

/** Text segment mode label (DESIGN.md §6.8). */
export function textModeBadge(t: Translate, start: string, end: string, e: EaseValue): string {
  switch (textSegmentMode(start, end, e)) {
    case 'switch':
      return t(m.badgeTextSwitch);
    case 'typeOn':
      return t(m.badgeTextTypeOn);
    case 'erase':
      return t(m.badgeTextErase);
    case 'extend':
      return t(m.badgeTextExtend);
    case 'counter':
      return t(m.badgeTextCounter);
  }
}
